import { initAssistantI18next } from "@domains/assistant/client/i18n/i18next.ts";
import { initConnectedAppsI18next } from "@domains/connected-apps/client/i18n/i18next.ts";
import { BREAKDOWN_QUERY_KEYS } from "@domains/reviews/client/components/breakdown/breakdownQueryState.ts";
import { CYCLE_SCORES_SERIES_QUERY_KEY } from "@domains/reviews/client/components/cycles/detail/breakdown/cycleBreakdownTabs.ts";
import { REVIEWEE_FILTER_QUERY_KEYS } from "@domains/reviews/client/components/reviewee-filters/revieweeFilterQueryKeys.ts";
import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import { initRoutinesI18next } from "@domains/routines/client/i18n/i18next.ts";
import { initSegmentsI18next } from "@domains/segments/client/i18n/i18next.ts";
import { BoltIcon, BuildingOffice2Icon, ClipboardDocumentListIcon, CreditCardIcon, DocumentTextIcon, PuzzlePieceIcon, ShieldCheckIcon, Squares2X2Icon, UserGroupIcon, UsersIcon } from "@heroicons/react/24/outline";
import { hasAnyRole, type Role } from "@packages/core/session/roles.ts";
import { withFeature } from "@packages/core/access/featureAccess.ts";
import { hasFeature } from "@packages/core/session/features.ts";
import { ADMIN_ONLY_ACCESS, GOOD_VIBES_COLLECTIONS_ACCESS, ORGANIZATION_PEOPLE_ACCESS, PERFORMANCE_CYCLE_DETAILS_ACCESS, PERFORMANCE_CYCLE_RESULTS_ACCESS, PERFORMANCE_CYCLES_ACCESS, REVIEW_CYCLE_CREATE_ACCESS } from "@packages/core/access/settingsAccess.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea, type NavGroupBuilder } from "@packages/core-client/registration/registerArea.tsx";
import type { FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import type { ReactElement } from "react";

import { initI18next } from "./i18n/i18next.ts";
import { GOOD_VIBES_COLLECTIONS_PATH, GOOD_VIBES_NEW_COLLECTION_PATH } from "./pages/features/featurePaths.ts";
import { FEATURE_ACCESS_SEGMENT, FeatureSegments } from "./pages/features/featureSegments.ts";
import { GOOD_VIBES_CREATE_CARD_SEGMENT, GOOD_VIBES_EDIT_CARD_SEGMENT } from "./pages/features/good-vibes/detail/goodVibesCardPaths.ts";
import { cycleBuilderAreaSegment, cycleCreateAreaSegment, cycleDetailAreaSegment, type CycleBuilderOrigin, type CycleDetailOrigin } from "@domains/reviews/client/components/cycles/cycleDetailPaths.ts";
import { REPORTING_LINE_SETUP_PATH } from "@domains/reporting-line/shared/reportingLineSetupPaths.ts";
import { SKILL_LIBRARY_SEGMENT } from "./pages/skill-library/skillLibraryPaths.ts";

const REGISTERED_CYCLE_DETAIL_ORIGINS = ["settings", "home"] as const satisfies readonly CycleDetailOrigin[];

async function loadFeatureAccessPage() {
    const { FeatureAccessPage } = await import("./pages/features/access/FeatureAccessPage.tsx");

    return { element: <FeatureAccessPage /> };
}

async function loadCompensationNextPage(page: Promise<ReactElement>) {
    const [{ CompensationNextGuard }, element] = await Promise.all([import("./pages/features/compensation/CompensationNextGuard.tsx"), page]);

    return { element: <CompensationNextGuard>{element}</CompensationNextGuard> };
}

async function loadPersonDetailsTab() {
    const { PersonDetailsPage } = await import("./pages/manage-people/detail/PersonDetailsPage.tsx");

    return { element: <PersonDetailsPage /> };
}

async function loadPersonDetailJobTab() {
    const { PersonDetailJobPage } = await import("./pages/manage-people/detail/PersonDetailJobPage.tsx");

    return { element: <PersonDetailJobPage /> };
}

async function loadPersonDetailPermissionsTab() {
    const { PersonDetailPermissionsPage } = await import("./pages/manage-people/detail/PersonDetailPermissionsPage.tsx");

    return { element: <PersonDetailPermissionsPage /> };
}

function registerManagePeopleDetailRoutes(people: NavGroupBuilder) {
    const personTabs = people.registerTabsArea("manage-people-detail", "/people/:personId", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        hideTitle: true,
        commandMenu: false,
        chrome: async () => (await import("./pages/manage-people/detail/personDetailTabsChrome.tsx")).PERSON_DETAIL_TABS_CHROME
    });

    const details = personTabs.registerRoute("details", {
        index: true,
        page: "AdminManagePersonDetails",
        lazy: loadPersonDetailsTab
    });
    const job = personTabs.registerRoute("job", {
        path: "job",
        page: "AdminManagePersonJob",
        lazy: loadPersonDetailJobTab
    });
    const permissions = personTabs.registerRoute("permissions", {
        path: "permissions",
        page: "AdminManagePersonPermissions",
        lazy: loadPersonDetailPermissionsTab
    });

    details.registerLink({ priority: 1000 });
    job.registerLink({ priority: 999 });
    permissions.registerLink({ priority: 998 });
}

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const { getRequestHandlers } = await import("./api/mocks/getRequestHandlers.ts");

        runtime.registerRequestHandlers(getRequestHandlers());
    }
}

function registerJobRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const jobArea = registerArea(runtime, "admin-settings-job", "/settings/jobs/:jobId", UsersIcon, i18nextInstance, { access: ORGANIZATION_PEOPLE_ACCESS });

    jobArea.registerPage({
        page: "AdminJobDetail",
        lazy: async () => {
            const { JobPage } = await import("./pages/jobs/job/JobPage.tsx");
            const { JobErrorBoundary } = await import("./pages/jobs/job/JobErrorBoundary.tsx");

            return { element: <JobPage />, errorElement: <JobErrorBoundary /> };
        }
    });
}

function registerGoodVibesCollectionRoute(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const newCollectionArea = registerArea(runtime, "admin-settings-good-vibes-collection-new", GOOD_VIBES_NEW_COLLECTION_PATH, UserGroupIcon, i18nextInstance, { access: GOOD_VIBES_COLLECTIONS_ACCESS });

    newCollectionArea.registerPage({
        page: "AdminFeatureGoodVibesCollectionNew",
        lazy: async () => {
            const { GoodVibesCreateCollectionPage } = await import("./pages/features/good-vibes/create/GoodVibesCreateCollectionPage.tsx");

            return { element: <GoodVibesCreateCollectionPage /> };
        }
    });

    const collectionArea = registerArea(runtime, "admin-settings-good-vibes-collection", `${GOOD_VIBES_COLLECTIONS_PATH}/:collectionId`, UserGroupIcon, i18nextInstance, { access: GOOD_VIBES_COLLECTIONS_ACCESS });

    const collectionTabs = collectionArea.registerTabsArea("main", {
        hideTitle: true,
        commandMenu: false,
        chrome: async () => (await import("./pages/features/good-vibes/detail/goodVibesCollectionTabsChrome.tsx")).GOOD_VIBES_COLLECTION_TABS_CHROME
    });

    const cards = collectionTabs.registerRoute("cards", {
        index: true,
        page: "AdminFeatureGoodVibesCollection",
        lazy: async () => {
            const { GoodVibesCollectionPage } = await import("./pages/features/good-vibes/detail/GoodVibesCollectionPage.tsx");

            return { element: <GoodVibesCollectionPage /> };
        }
    });
    cards.registerOverlay("create-card", {
        segment: GOOD_VIBES_CREATE_CARD_SEGMENT,
        page: "AdminFeatureGoodVibesCardCreate",
        lazy: async () => {
            const { GoodVibesCardCreateRoute } = await import("./pages/features/good-vibes/detail/GoodVibesCardCreateRoute.tsx");

            return { element: <GoodVibesCardCreateRoute /> };
        }
    });
    cards.registerOverlay("edit-card", {
        segment: GOOD_VIBES_EDIT_CARD_SEGMENT,
        page: "AdminFeatureGoodVibesCardEdit",
        lazy: async () => {
            const { GoodVibesCardEditRoute } = await import("./pages/features/good-vibes/detail/GoodVibesCardEditRoute.tsx");

            return { element: <GoodVibesCardEditRoute /> };
        }
    });
    const details = collectionTabs.registerRoute("details", {
        path: "details",
        page: "AdminFeatureGoodVibesCollectionDetails",
        lazy: async () => {
            const { GoodVibesCollectionDetailsPage } = await import("./pages/features/good-vibes/detail/GoodVibesCollectionDetailsPage.tsx");

            return { element: <GoodVibesCollectionDetailsPage /> };
        }
    });

    cards.registerLink({ priority: 1000 });
    details.registerLink({ priority: 999 });
}

async function loadPeopleFieldPage() {
    const { PeopleFieldPage } = await import("./pages/people-fields/field/PeopleFieldPage.tsx");

    return { element: <PeopleFieldPage /> };
}

async function loadPeopleSyncSettingsPage() {
    const { PeopleSyncSettingsPage } = await import("./pages/integrations/people-sync/settings/PeopleSyncSettingsPage.tsx");

    return { element: <PeopleSyncSettingsPage /> };
}

function registerPeopleSyncSettingsRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const peopleSyncSettingsArea = registerArea(runtime, "admin-settings-people-sync-settings", "/settings/integrations/people-sync/:provider", BoltIcon, i18nextInstance, { access: ADMIN_ONLY_ACCESS });

    peopleSyncSettingsArea.registerPage({ page: "AdminIntegrationsPeopleSyncSettings", lazy: loadPeopleSyncSettingsPage });

    const peopleSyncHrisSettingsArea = registerArea(runtime, "admin-settings-people-sync-settings-hris", "/settings/integrations/people-sync/hris/:hrisSlug", BoltIcon, i18nextInstance, { access: ADMIN_ONLY_ACCESS });

    peopleSyncHrisSettingsArea.registerPage({ page: "AdminIntegrationsPeopleSyncSettings", lazy: loadPeopleSyncSettingsPage });
}

function registerPeopleFieldRoutes(people: NavGroupBuilder) {
    people.registerRoute("people-field-new", {
        path: "/people-fields/new",
        page: "AdminNewPeopleField",
        noHeading: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadPeopleFieldPage
    });

    people.registerRoute("people-field", {
        path: "/people-fields/:fieldId",
        page: "AdminPeopleField",
        noHeading: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadPeopleFieldPage
    });
}

function registerReviewCycleDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n, origin: CycleDetailOrigin) {
    const cycleDetailArea = registerArea(runtime, `review-cycle-detail-${origin}`, cycleDetailAreaSegment(origin), ClipboardDocumentListIcon, i18nextInstance, { access: withFeature("PerformanceReviews", PERFORMANCE_CYCLE_DETAILS_ACCESS) });

    const cycleTabs = cycleDetailArea.registerTabsArea("main", {
        hideTitle: true,
        commandMenu: false,
        preserveSearchParams: [...REVIEWEE_FILTER_QUERY_KEYS, ...BREAKDOWN_QUERY_KEYS, CYCLE_SCORES_SERIES_QUERY_KEY],
        chrome: async () => (await import("./pages/review-cycles/reviewCycleDetailTabsChrome.tsx")).reviewCycleDetailTabsChrome(origin)
    });

    const progression = cycleTabs.registerRoute("progression", {
        index: true,
        page: "ReviewCycleProgression",
        lazy: async () => {
            const { ReviewCycleProgressionPage } = await import("./pages/review-cycles/ReviewCycleProgressionPage.tsx");

            return { element: <ReviewCycleProgressionPage origin={origin} /> };
        }
    });

    const resultsAccess = { access: withFeature("PerformanceReviews", PERFORMANCE_CYCLE_RESULTS_ACCESS) };

    const scores = cycleTabs.registerRoute("scores", {
        path: "scores",
        page: "ReviewCycleScores",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleScoresPage } = await import("./pages/review-cycles/ReviewCycleScoresPage.tsx");

            return { element: <ReviewCycleScoresPage origin={origin} /> };
        }
    });
    const calibration = cycleTabs.registerRoute("calibration", {
        path: "calibration",
        page: "ReviewCycleCalibration",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleCalibrationPage } = await import("./pages/review-cycles/ReviewCycleCalibrationPage.tsx");

            return { element: <ReviewCycleCalibrationPage origin={origin} /> };
        }
    });
    const questions = cycleTabs.registerRoute("questions", {
        path: "questions",
        page: "ReviewCycleQuestionResults",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleQuestionResultsPage } = await import("./pages/review-cycles/ReviewCycleQuestionResultsPage.tsx");

            return { element: <ReviewCycleQuestionResultsPage /> };
        }
    });
    const themes = cycleTabs.registerRoute("themes", {
        path: "themes",
        page: "ReviewCycleThemes",
        ...resultsAccess,
        lazy: async () => {
            const { ReviewCycleThemesPage } = await import("./pages/review-cycles/ReviewCycleThemesPage.tsx");

            return { element: <ReviewCycleThemesPage /> };
        }
    });

    progression.registerLink({ priority: 1000 });

    return { scores, calibration, questions, themes };
}

function registerReviewCycleBuilderRoutes(runtime: FireflyRuntime, i18nextInstance: i18n, origin: CycleBuilderOrigin) {
    const builderArea = registerArea(runtime, `review-cycle-builder-${origin}`, cycleBuilderAreaSegment(origin), ClipboardDocumentListIcon, i18nextInstance, { access: REVIEW_CYCLE_CREATE_ACCESS });

    builderArea.registerPage({
        page: "ReviewCycleBuilder",
        fullHeight: true,
        lazy: async () => {
            const { ReviewCycleBuilderPage } = await import("./pages/review-cycles/ReviewCycleBuilderPage.tsx");

            return { element: <ReviewCycleBuilderPage origin={origin} /> };
        }
    });

    const createArea = registerArea(runtime, `review-cycle-new-${origin}`, cycleCreateAreaSegment(origin), ClipboardDocumentListIcon, i18nextInstance, { access: REVIEW_CYCLE_CREATE_ACCESS });

    createArea.registerPage({
        page: "ReviewCycleCreate",
        lazy: async () => {
            if (origin === "settings") {
                const { Navigate } = await import("react-router");

                return { element: <Navigate to={cycleCreateAreaSegment("org")} replace /> };
            }

            const { NewReviewCyclePage } = await import("./pages/review-cycles/NewReviewCyclePage.tsx");

            return { element: <NewReviewCyclePage origin={origin} /> };
        }
    });
}

function registerJobArchitectureAiSetupRoute(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const jobArchitectureAiSetupArea = registerArea(runtime, "job-architecture-ai-setup", "/settings/jobs/get-started/ai", UserGroupIcon, i18nextInstance, { access: ORGANIZATION_PEOPLE_ACCESS });

    jobArchitectureAiSetupArea.registerPage({
        page: "AdminJobsArchitectureAi",
        fullHeight: true,
        lazy: async () => {
            const { JobArchitectureAiSetupPage } = await import("./pages/jobs/ai-setup/JobArchitectureAiSetupPage.tsx");

            return { element: <JobArchitectureAiSetupPage /> };
        }
    });
}

function registerReportingLineSetupRoute(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reportingLineSetupArea = registerArea(runtime, "reporting-line-setup", REPORTING_LINE_SETUP_PATH, UserGroupIcon, i18nextInstance, { access: ORGANIZATION_PEOPLE_ACCESS });

    reportingLineSetupArea.registerPage({
        page: "AdminReportingLineSetup",
        fullHeight: true,
        immersive: true,
        lazy: async () => {
            const { ReportingLineSetupPage } = await import("./pages/reporting-line-setup/ReportingLineSetupPage.tsx");

            return { element: <ReportingLineSetupPage /> };
        }
    });
}

export const registerAdminSettings: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initAssistantI18next(runtime);
    initConnectedAppsI18next(runtime);
    initSegmentsI18next(runtime);
    initReviewCyclesI18next(runtime);
    initRoutinesI18next(runtime);

    await registerMsw(runtime);

    registerReviewCycleBuilderRoutes(runtime, i18nextInstance, "org");

    const cycleResultsTabs = REGISTERED_CYCLE_DETAIL_ORIGINS.map(origin => {
        registerReviewCycleBuilderRoutes(runtime, i18nextInstance, origin);

        return registerReviewCycleDetailRoutes(runtime, i18nextInstance, origin);
    });

    registerJobArchitectureAiSetupRoute(runtime, i18nextInstance);
    registerReportingLineSetupRoute(runtime, i18nextInstance);
    registerJobRoutes(runtime, i18nextInstance);
    registerGoodVibesCollectionRoute(runtime, i18nextInstance);
    registerPeopleSyncSettingsRoutes(runtime, i18nextInstance);

    const area = registerArea(runtime, "admin-settings", "/settings", BuildingOffice2Icon, i18nextInstance);

    const organization = area.registerSidebarArea("organization");

    organization.registerRoute("integrations-sso-slack", {
        path: "/integrations/sso/slack",
        page: "AdminIntegrationsSingleSignOnSlack",
        access: ADMIN_ONLY_ACCESS,
        noHeading: true,
        lazy: async () => {
            const { SlackSsoPage } = await import("./pages/integrations/sso/slack/SlackSsoPage.tsx");

            return { element: <SlackSsoPage /> };
        }
    });

    organization.registerRoute("integrations-notifications-slack", {
        path: "/integrations/notifications/slack",
        page: "AdminIntegrationsNotificationsSlack",
        access: ADMIN_ONLY_ACCESS,
        noHeading: true,
        lazy: async () => {
            const { SlackNotificationsPage } = await import("./pages/integrations/notifications/slack/SlackNotificationsPage.tsx");

            return { element: <SlackNotificationsPage /> };
        }
    });

    organization.registerRoute("integrations-notifications-microsoft-teams", {
        path: "/integrations/notifications/microsoft-teams",
        page: "AdminIntegrationsNotificationsMicrosoftTeams",
        access: ADMIN_ONLY_ACCESS,
        noHeading: true,
        lazy: async () => {
            const { TeamsNotificationsPage } = await import("./pages/integrations/notifications/microsoft-teams/TeamsNotificationsPage.tsx");

            return { element: <TeamsNotificationsPage /> };
        }
    });

    const company = organization.registerTabsArea("company", "/company-info", {
        landing: true,
        access: ADMIN_ONLY_ACCESS
    });

    const companyProfile = company.registerRoute("profile", {
        index: true,
        page: "AdminOrganizationCompanyProfile",
        lazy: async () => {
            const { CompanyProfilePage } = await import("./pages/company/CompanyProfilePage.tsx");

            return { element: <CompanyProfilePage /> };
        }
    });

    const companyMissionAndValues = company.registerRoute("mission-and-values", {
        path: "/mission-and-values",
        page: "AdminOrganizationCompanyMissionAndValues",
        lazy: async () => {
            const { CompanyMissionAndValuesPage } = await import("./pages/company/CompanyMissionAndValuesPage.tsx");

            return { element: <CompanyMissionAndValuesPage /> };
        }
    });

    companyProfile.registerLink({ priority: 1000 });
    companyMissionAndValues.registerLink({ priority: 999 });

    const featuresList = organization.registerRoute("features", {
        path: "/features",
        page: "AdminFeatures",
        access: [...ORGANIZATION_PEOPLE_ACCESS, ...PERFORMANCE_CYCLES_ACCESS],
        lazy: async () => {
            const { FeaturesPage } = await import("./pages/features/list/FeaturesPage.tsx");

            return { element: <FeaturesPage /> };
        }
    });

    const people = organization.registerNavGroup("people", { path: "/people" });

    const managePeople = people.registerRoute("manage-people", {
        path: "/people",
        page: "AdminManagePeople",
        landing: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { PeoplePage } = await import("./pages/PeoplePage.tsx");

            return { element: <PeoplePage /> };
        }
    });

    const peopleFields = people.registerRoute("people-fields", {
        path: "/people-fields",
        page: "AdminPeopleFields",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { PeopleFieldsPage } = await import("./pages/PeopleFieldsPage.tsx");

            return { element: <PeopleFieldsPage /> };
        }
    });

    registerPeopleFieldRoutes(people);
    registerManagePeopleDetailRoutes(people);

    const jobs = people.registerRoute("jobs", {
        path: "/jobs",
        page: "AdminJobs",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { JobsPage } = await import("./pages/jobs/JobsPage.tsx");

            return { element: <JobsPage /> };
        }
    });

    people.registerRoute("job-new", {
        path: "/jobs/new",
        page: "AdminJobCreate",
        noHeading: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { NewJobPage } = await import("./pages/jobs/NewJobPage.tsx");

            return { element: <NewJobPage /> };
        }
    });

    people.registerRoute("jobs-get-started", {
        path: "/jobs/get-started",
        page: "AdminJobsGetStarted",
        noHeading: true,
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: async () => {
            const { JobArchitectureGetStartedPage } = await import("./pages/jobs/JobArchitectureGetStartedPage.tsx");

            return { element: <JobArchitectureGetStartedPage /> };
        }
    });

    people.registerRoute("job-architecture", {
        path: "/jobs/architecture",
        page: "AdminJobArchitecture",
        access: ORGANIZATION_PEOPLE_ACCESS,
        noHeading: true,
        lazy: async () => {
            const { JobArchitecturePage } = await import("./pages/jobs/JobArchitecturePage.tsx");

            return { element: <JobArchitecturePage /> };
        }
    });

    const segments = people.registerTabsArea("segments", "/segments", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        preserveSearchParams: ["query"],
        chrome: async () => (await import("./pages/segments/segmentsTabsChrome.ts")).SEGMENTS_TABS_CHROME
    });

    const mySegments = segments.registerRoute("mine", {
        index: true,
        page: "AdminSegmentsMine",
        lazy: async () => {
            const { MySegmentsPage } = await import("./pages/segments/list/MySegmentsPage.tsx");

            return { element: <MySegmentsPage /> };
        }
    });

    const sharedWithMeSegments = segments.registerRoute("shared-with-me", {
        path: "/shared-with-me",
        page: "AdminSegmentsSharedWithMe",
        lazy: async () => {
            const { SharedWithMeSegmentsPage } = await import("./pages/segments/list/SharedWithMeSegmentsPage.tsx");

            return { element: <SharedWithMeSegmentsPage /> };
        }
    });

    mySegments.registerLink({ priority: 1000 });
    sharedWithMeSegments.registerLink({ priority: 999 });

    const newSegment = people.registerTabsArea("segment-new", "/segments/new", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        commandMenu: false,
        chrome: async () => ({ useRenderAsTitleOnly: () => true })
    });

    newSegment.registerRoute("view", {
        index: true,
        page: "AdminSegmentCreate",
        lazy: async () => {
            const { NewSegmentPage } = await import("./pages/segments/form/NewSegmentPage.tsx");

            return { element: <NewSegmentPage /> };
        }
    });

    const editSegment = people.registerTabsArea("segment-edit", "/segments/:segmentId", {
        access: ORGANIZATION_PEOPLE_ACCESS,
        commandMenu: false,
        chrome: async () => (await import("./pages/segments/form/segmentFormTabsChrome.tsx")).SEGMENT_FORM_TABS_CHROME
    });

    editSegment.registerRoute("view", {
        index: true,
        page: "AdminSegmentEdit",
        lazy: async () => {
            const { EditSegmentPage } = await import("./pages/segments/form/EditSegmentPage.tsx");

            return { element: <EditSegmentPage /> };
        }
    });

    const rolesAndPermissions = organization.registerRoute("roles-and-permissions", {
        path: "/permissions",
        page: "AdminRolesAndPermissions",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { RolesAndPermissionsPage } = await import("./pages/RolesAndPermissionsPage.tsx");

            return { element: <RolesAndPermissionsPage /> };
        }
    });

    const integrations = organization.registerTabsArea("integrations", "/integrations", {
        access: ADMIN_ONLY_ACCESS
    });

    const integrationsPeopleSync = integrations.registerRoute("people-sync", {
        index: true,
        page: "AdminIntegrations",
        lazy: async () => {
            const { IntegrationsPeopleSyncPage } = await import("./pages/integrations/IntegrationsPeopleSyncPage.tsx");

            return { element: <IntegrationsPeopleSyncPage /> };
        }
    });

    const integrationsNotifications = integrations.registerRoute("notifications", {
        path: "/notifications",
        page: "AdminIntegrationsNotifications",
        lazy: async () => {
            const { IntegrationsNotificationsPage } = await import("./pages/integrations/IntegrationsNotificationsPage.tsx");

            return { element: <IntegrationsNotificationsPage /> };
        }
    });

    const integrationsSso = integrations.registerRoute("sso", {
        path: "/sso",
        page: "AdminIntegrationsSingleSignOn",
        lazy: async () => {
            const { IntegrationsSsoPage } = await import("./pages/integrations/IntegrationsSsoPage.tsx");

            return { element: <IntegrationsSsoPage /> };
        }
    });

    const billing = organization.registerRoute("billing", {
        path: "/billing",
        page: "AdminBilling",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { BillingPage } = await import("./pages/BillingPage.tsx");

            return { element: <BillingPage /> };
        }
    });

    const agent = area.registerSidebarArea("agent");

    const skillLibrary = agent.registerRoute("skill-library", {
        path: SKILL_LIBRARY_SEGMENT,
        page: "AdminAgentSkillLibrary",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { SkillLibraryPage } = await import("./pages/skill-library/list/SkillLibraryPage.tsx");

            return { element: <SkillLibraryPage /> };
        }
    });
    const automations = agent.registerRoute("automations", {
        path: "/agent/routines",
        page: "AdminAgentAutomations",
        access: ADMIN_ONLY_ACCESS,
        lazy: async () => {
            const { AutomationsPage } = await import("./pages/automations/AutomationsPage.tsx");

            return { element: <AutomationsPage /> };
        }
    });
    const newSkill = agent.registerTabsArea("skill-new", `${SKILL_LIBRARY_SEGMENT}/new`, {
        access: ADMIN_ONLY_ACCESS,
        commandMenu: false,
        chrome: async () => ({ useRenderAsTitleOnly: () => true })
    });

    newSkill.registerRoute("view", {
        index: true,
        page: "AdminAgentSkillCreate",
        lazy: async () => {
            const { NewSkillPage } = await import("./pages/skill-library/new/NewSkillPage.tsx");

            return { element: <NewSkillPage /> };
        }
    });

    const skillDetail = agent.registerTabsArea("skill-detail", `${SKILL_LIBRARY_SEGMENT}/:skillSlug`, {
        access: ADMIN_ONLY_ACCESS,
        commandMenu: false,
        chrome: async () => (await import("./pages/skill-library/skillDetailTabsChrome.tsx")).SKILL_DETAIL_TABS_CHROME
    });

    skillDetail.registerRoute("view", {
        index: true,
        page: "AdminFeatureSkillDetail",
        lazy: async () => {
            const { SkillDetailPage } = await import("./pages/skill-library/detail/SkillDetailPage.tsx");

            return { element: <SkillDetailPage /> };
        }
    });

    const customSkill = agent.registerTabsArea("skill-custom", `${SKILL_LIBRARY_SEGMENT}/custom/:skillSlug`, {
        access: ADMIN_ONLY_ACCESS,
        commandMenu: false,
        chrome: async () => (await import("./pages/skill-library/skillDetailTabsChrome.tsx")).SKILL_DETAIL_TABS_CHROME
    });

    customSkill.registerRoute("view", {
        index: true,
        page: "AdminAgentSkillEdit",
        lazy: async () => {
            const { EditCustomSkillPage } = await import("./pages/skill-library/edit/EditCustomSkillPage.tsx");

            return { element: <EditCustomSkillPage /> };
        }
    });

    const connectedApps = agent.registerTabsArea("connected-apps", "/agent/connected-apps", {
        access: ADMIN_ONLY_ACCESS
    });

    const connectedAppsKnowledgeSources = connectedApps.registerRoute("knowledge-sources", {
        index: true,
        page: "AdminAgentConnectedAppsKnowledgeSources",
        lazy: async () => {
            const { AdminConnectedAppsKnowledgeSourcesPage } = await import("./pages/connected-apps/AdminConnectedAppsKnowledgeSourcesPage.tsx");

            return { element: <AdminConnectedAppsKnowledgeSourcesPage /> };
        }
    });

    const connectedAppsAccess = connectedApps.registerRoute("access", {
        path: "/access",
        page: "AdminAgentConnectedAppsAccess",
        lazy: async () => {
            const { AdminConnectedAppsAccessPage } = await import("./pages/connected-apps/AdminConnectedAppsAccessPage.tsx");

            return { element: <AdminConnectedAppsAccessPage /> };
        }
    });

    const features = area.registerSidebarArea("features");

    const compensation = features.registerTabsArea("compensation", FeatureSegments.compensation, {
        access: ORGANIZATION_PEOPLE_ACCESS,
        commandMenu: false,
        chrome: async () => (await import("./pages/features/compensation/compensationNextTabsChrome.ts")).COMPENSATION_NEXT_TABS_CHROME
    });

    compensation.registerRoute("compensation-landing", {
        index: true,
        lazy: () => loadCompensationNextPage(import("./pages/features/compensation/CompensationLandingPage.tsx").then(({ CompensationLandingPage }) => <CompensationLandingPage />))
    });

    const compensationPayZones = compensation.registerRoute("pay-zones", {
        path: "/pay-zones",
        page: "AdminFeatureCompensationPayZones",
        lazy: () => loadCompensationNextPage(import("./pages/features/compensation/CompensationPayZonesPage.tsx").then(({ CompensationPayZonesPage }) => <CompensationPayZonesPage />))
    });

    const compensationCurrencies = compensation.registerRoute("currencies", {
        path: "/currencies",
        page: "AdminFeatureCompensationCurrencies",
        lazy: () => loadCompensationNextPage(import("./pages/features/compensation/CompensationCurrenciesPage.tsx").then(({ CompensationCurrenciesPage }) => <CompensationCurrenciesPage />))
    });

    const compensationAccess = compensation.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeatureCompensationAccess",
        lazy: () => loadCompensationNextPage(import("./pages/features/access/FeatureAccessPage.tsx").then(({ FeatureAccessPage }) => <FeatureAccessPage />))
    });

    const compensationClassic = features.registerTabsArea("compensation-classic", FeatureSegments["compensation-classic"], { access: ORGANIZATION_PEOPLE_ACCESS, commandMenu: false });

    const compensationClassicAccess = compensationClassic.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeatureCompensationClassicAccess",
        lazy: loadFeatureAccessPage
    });

    const engagement = features.registerTabsArea("engagement", FeatureSegments.engagement, { access: ORGANIZATION_PEOPLE_ACCESS, commandMenu: false });

    const engagementSettings = engagement.registerRoute("settings", {
        index: true,
        page: "AdminFeatureEngagementSettings",
        lazy: async () => {
            const { EngagementSettingsPage } = await import("./pages/features/engagement/EngagementSettingsPage.tsx");

            return { element: <EngagementSettingsPage /> };
        }
    });

    const engagementAccess = engagement.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeatureEngagementAccess",
        lazy: loadFeatureAccessPage
    });

    const goals = features.registerTabsArea("goals", FeatureSegments.goals, {
        landing: true,
        access: [...PERFORMANCE_CYCLES_ACCESS, ...ORGANIZATION_PEOPLE_ACCESS],
        commandMenu: false
    });

    const goalsSettings = goals.registerRoute("settings", {
        index: true,
        page: "AdminFeatureGoals",
        lazy: async () => {
            const { GoalsFeatureIndexPage } = await import("./pages/features/goals/GoalsFeatureIndexPage.tsx");

            return { element: <GoalsFeatureIndexPage /> };
        }
    });

    const goalsAccess = goals.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeatureGoalsAccess",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    const goodVibes = features.registerTabsArea("good-vibes", FeatureSegments["good-vibes"], { access: ORGANIZATION_PEOPLE_ACCESS, commandMenu: false });

    const goodVibesSettings = goodVibes.registerRoute("settings", {
        index: true,
        page: "AdminFeatureGoodVibesSettings",
        lazy: async () => {
            const { GoodVibesSettingsPage } = await import("./pages/features/good-vibes/GoodVibesSettingsPage.tsx");

            return { element: <GoodVibesSettingsPage /> };
        }
    });

    const goodVibesCollections = goodVibes.registerRoute("collections", {
        path: "/collections",
        page: "AdminFeatureGoodVibesCollections",
        access: GOOD_VIBES_COLLECTIONS_ACCESS,
        lazy: async () => {
            const { GoodVibesCollectionsPage } = await import("./pages/features/good-vibes/GoodVibesCollectionsPage.tsx");

            return { element: <GoodVibesCollectionsPage /> };
        }
    });

    const goodVibesAccess = goodVibes.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeatureGoodVibesAccess",
        lazy: loadFeatureAccessPage
    });

    features.registerRoute("meetings", {
        path: FeatureSegments.meetings,
        page: "AdminFeatureMeetings",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    features.registerRoute("org-chart", {
        path: FeatureSegments["org-chart"],
        page: "AdminFeatureOrgChart",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    const performanceReviews = features.registerTabsArea("performance-reviews", FeatureSegments["performance-reviews"], {
        access: [...PERFORMANCE_CYCLES_ACCESS, ...ORGANIZATION_PEOPLE_ACCESS],
        commandMenu: false
    });

    performanceReviews.registerRoute("index", {
        index: true,
        lazy: async () => {
            const { PerformanceReviewsIndexRedirect } = await import("./pages/features/performance-reviews/PerformanceReviewsIndexRedirect.tsx");

            return { element: <PerformanceReviewsIndexRedirect /> };
        }
    });

    performanceReviews.registerRoute("access", {
        path: FEATURE_ACCESS_SEGMENT,
        page: "AdminFeaturePerformanceReviewsAccess",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    features.registerRoute("plans", {
        path: FeatureSegments.plans,
        page: "AdminFeaturePlans",
        access: ORGANIZATION_PEOPLE_ACCESS,
        lazy: loadFeatureAccessPage
    });

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        const canSee = (roles: readonly Role[]) => hasAnyRole(session, roles);
        const isAdmin = canSee(ADMIN_ONLY_ACCESS);
        const canManageFeatureAccess = deferredRuntime.getFeatureFlag("manage-feature-access", false) && canSee(ORGANIZATION_PEOPLE_ACCESS);
        const canSetGoals = canSee(PERFORMANCE_CYCLES_ACCESS);
        const hasReviews = hasFeature("PerformanceReviews")(session);

        if (isAdmin) {
            company.registerLink({ priority: 200, icon: BuildingOffice2Icon });
        }

        if (canManageFeatureAccess || canSetGoals) {
            featuresList.registerLink({ priority: 195, icon: Squares2X2Icon });
        }

        if (canManageFeatureAccess && canSetGoals) {
            goalsSettings.registerLink({ priority: 1000 });
            goalsAccess.registerLink({ priority: 999 });
        }

        if (canManageFeatureAccess && deferredRuntime.getFeatureFlag("show-compensation-next", false)) {
            if (deferredRuntime.getFeatureFlag("cmp-settings-pay-zones", false)) {
                compensationPayZones.registerLink({ priority: 1000 });
            }

            if (deferredRuntime.getFeatureFlag("cmp-settings-currencies", false)) {
                compensationCurrencies.registerLink({ priority: 999 });
            }

            compensationAccess.registerLink({ priority: 998 });
        }

        if (canManageFeatureAccess) {
            compensationClassicAccess.registerLink({ priority: 998 });
        }

        if (canManageFeatureAccess) {
            engagementSettings.registerLink({ priority: 1000 });
            engagementAccess.registerLink({ priority: 999 });

            if (!deferredRuntime.getFeatureFlag("good-vibes-settings", false)) {
                goodVibesSettings.registerLink({ priority: 1000 });
            } else if (canSee(GOOD_VIBES_COLLECTIONS_ACCESS)) {
                goodVibesCollections.registerLink({ priority: 1000 });
            }
            goodVibesAccess.registerLink({ priority: 999 });
        }

        if (canSee(ORGANIZATION_PEOPLE_ACCESS)) {
            people.registerLink({ priority: 190, icon: UsersIcon });
            managePeople.registerLink({ priority: 30 });
            peopleFields.registerLink({ priority: 20 });
            jobs.registerLink({ priority: 10 });

            segments.registerLink({ priority: 5 });
        }

        if (isAdmin) {
            rolesAndPermissions.registerLink({
                priority: 160,
                icon: ShieldCheckIcon
            });
            integrations.registerLink({ priority: 150, icon: BoltIcon });

            integrationsPeopleSync.registerLink({ priority: 1000, commandMenu: false });
            integrationsNotifications.registerLink({ priority: 999 });
            integrationsSso.registerLink({ priority: 998 });

            billing.registerLink({ priority: 140, icon: CreditCardIcon });

            skillLibrary.registerLink({ priority: 90, icon: DocumentTextIcon });

            if (deferredRuntime.getFeatureFlag("weekly-brief", false)) {
                automations.registerLink({ priority: 85, icon: BoltIcon });
            }

            connectedApps.registerLink({
                priority: 80,
                icon: PuzzlePieceIcon
            });

            connectedAppsKnowledgeSources.registerLink({ priority: 1000, commandMenu: false });
            connectedAppsAccess.registerLink({ priority: 999, commandMenu: true });
        }

        if (canSee(PERFORMANCE_CYCLE_RESULTS_ACCESS) && hasReviews) {
            for (const { scores, calibration, questions, themes } of cycleResultsTabs) {
                scores.registerLink({ priority: 999 });
                calibration.registerLink({ priority: 998 });
                questions.registerLink({ priority: 997 });
                themes.registerLink({ priority: 996 });
            }
        }
    };
};
